/**
 * useOptimizedVirtual - Optimized virtualization hook for table rows
 *
 * Uses refs for row data so that estimateSize / getVirtualItems / getTotalSize
 * callbacks remain stable across renders even when the rows array changes identity.
 */

import { useVirtualizer } from '@tanstack/react-virtual';
import type { VirtualItem } from '@tanstack/react-virtual';
import type { Row } from '@tanstack/react-table';
import { useCallback, useEffect, useRef } from 'react';

export type VirtualRowItem<T> = Row<T> | { isExpandedRow: boolean; parentRow: Row<T> };

export type UseOptimizedVirtualOptions<T> = {
  /** When true, virtualization is disabled and all rows are rendered */
  disabled?: boolean;
  /** Height of each row in pixels */
  rowHeight: number;
  /** Height of expanded rows - can be a number or function */
  expandedRowHeight?: number | ((row?: Row<T>) => number);
  /** Overscan value - defaults to 100 for smooth scrolling */
  overscan?: number;
};

/**
 * Optimized virtualization hook with higher overscan for smooth scrolling.
 * Row data is held in a ref so callbacks don't recreate when the rows array changes.
 */
export const useOptimizedVirtual = <T>(
  rows: VirtualRowItem<T>[],
  container: React.MutableRefObject<HTMLDivElement | null>,
  options: UseOptimizedVirtualOptions<T>
) => {
  const { disabled = false, rowHeight, expandedRowHeight, overscan = 20 } = options;

  // ── Refs to avoid recreating callbacks when rows / options change ──
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const rowHeightRef = useRef(rowHeight);
  rowHeightRef.current = rowHeight;

  const expandedRowHeightRef = useRef(expandedRowHeight);
  expandedRowHeightRef.current = expandedRowHeight;

  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  // Stable getScrollElement
  const getScrollElement = useCallback(() => container.current, [container]);

  // Stable estimateSize – reads from refs
  const estimateSize = useCallback(
    (index: number) => {
      const row = rowsRef.current[index];
      if (row && typeof row === 'object' && 'isExpandedRow' in row) {
        const erh = expandedRowHeightRef.current;
        if (typeof erh === 'function') {
          return erh(row?.parentRow);
        }
        return erh ?? rowHeightRef.current;
      }
      return rowHeightRef.current;
    },
    [] // stable – reads from refs
  );

  const virtualizer = useVirtualizer({
    getScrollElement,
    count: rows.length,
    estimateSize,
    overscan,
  });

  // Recalculate sizes when rowHeight changes
  const prevRowHeight = useRef(rowHeight);
  useEffect(() => {
    if (prevRowHeight.current !== rowHeight) {
      virtualizer.measure();
      prevRowHeight.current = rowHeight;
    }
  }, [rowHeight, virtualizer]);

  // Stable getVirtualItems – reads from refs
  const getVirtualItems = useCallback((): VirtualItem[] => {
    if (disabledRef.current) {
      const currentRows = rowsRef.current;
      const rh = rowHeightRef.current;
      const erh = expandedRowHeightRef.current;
      let currentStart = 0;
      return currentRows.map((row, index) => {
        const isExpandedRow = typeof row === 'object' && 'isExpandedRow' in row;
        const itemHeight = isExpandedRow
          ? typeof erh === 'function'
            ? erh(row?.parentRow)
            : erh ?? rh
          : rh;
        const virtualItem = {
          key: isExpandedRow ? `${row.parentRow.id}-expanded` : row.id,
          index,
          start: currentStart,
          end: currentStart + itemHeight,
          size: itemHeight,
          lane: 0,
        };
        currentStart += itemHeight;
        return virtualItem;
      });
    }
    return virtualizer.getVirtualItems();
  }, [virtualizer]);

  // Stable getTotalSize – reads from refs
  const getTotalSize = useCallback(() => {
    if (disabledRef.current) {
      const currentRows = rowsRef.current;
      const rh = rowHeightRef.current;
      const erh = expandedRowHeightRef.current;
      return currentRows.reduce((total, row) => {
        const isExpandedRow = typeof row === 'object' && 'isExpandedRow' in row;
        const itemHeight = isExpandedRow
          ? typeof erh === 'function'
            ? erh(row.parentRow)
            : erh ?? rh
          : rh;
        return total + itemHeight;
      }, 0);
    }
    return virtualizer.getTotalSize();
  }, [virtualizer]);

  return { getVirtualItems, getTotalSize };
};
